import { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQueryClient } from 'react-query';
import useHasPermission from '../../../hooks/useHasPermission';
import useWaitlistApplicationWindow from '../../../hooks/useWaitlistApplicationWindow';
import {
  useFetchAffordableWaitlist,
  useFetchWaitlistConfiguration,
} from '../../../hooks/data-fetching/useFetchWaitlistV2';
// eslint-disable-next-line max-len
import useAffordableWaitlistValidations from '../../ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';
import { invalidateAffordableQualificationQueries } from '../../GeneralAffordable/hooks';
import messages from './messages';
import { formatNBedsArr } from '../../../utils/prospectPreferences-helpers';
import {
  CONVENTIONAL_PROGRAM_NAME,
  toAtomicProgramNames,
} from '../../../utils/affordable';

/**
 * The per-program income levels the server scored, as rows this tab renders. Which band a
 * set-aside names is the server's to decide — the Convert to Applicant modal reads the same
 * answer, and a second copy of that scoring here would let the two screens disagree.
 *
 * A household with no waitlist record was never scored, so the rows come from the programs the
 * waitlist configuration covers and every level reads as a dash. The caller hands those names in
 * already split to individual programs: the configuration only ever holds individual names, so a
 * layered `LIHTC/HOME` would render a row nothing can match. That split is the one layering rule
 * this file applies, and it applies to the unscored branch alone.
 */
export const toIncomeLevelRows = (incomeLevels, programNames) => {
  const scored =
    incomeLevels ??
    (programNames ?? []).map((programName) => ({
      programName,
      incomeLevel: null,
    }));

  return [
    ...scored.map(({ programName, incomeLevel }) => ({
      programName,
      level: incomeLevel,
    })),
    // Conventional units aren't income-restricted, so the server never scores them — always
    // render as a dash per the Figma design.
    { programName: CONVENTIONAL_PROGRAM_NAME, level: null },
  ];
};

export const formatWaitlistPreferences = (preferences) => {
  if (!preferences) return null;
  const formatted = Object.entries(preferences)
    .filter(([, value]) => value)
    .map(([key]) =>
      key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (char) => char.toUpperCase()),
    )
    .join(', ');
  return formatted || null;
};

export const mapAffordableWaitlistRanks = (positions) =>
  (positions ?? []).map((position) => ({
    bedroomCount: position.bedroomCount,
    rank: position.rank,
    affordableWaitlistId: position.affordableWaitlistId,
    applicationId: position.applicationId,
    id: position.id,
    status: position.status,
  }));

export const getPreferredBedroomsText = (prospectPreferences) =>
  formatNBedsArr(prospectPreferences?.nBedsArr, prospectPreferences?.nBeds);

export const getPreferredFloorPlanName = (prospectPreferences, floorPlans) => {
  const floorPlanId = prospectPreferences?.preferredFloorPlanId;
  if (!floorPlanId) return null;
  const floorPlan = (floorPlans ?? []).find((fp) => fp.id === floorPlanId);
  return floorPlan?.internalName ?? floorPlan?.marketingName ?? null;
};

export function useWaitlistSummary(
  organizationId,
  propertyId,
  applicationId,
  prospectPreferences,
  floorPlans,
) {
  const { affordableWaitlistV2 } = useFlags();

  const { data: waitlistConfigurationData, isLoading: isConfigurationLoading } =
    useFetchWaitlistConfiguration(propertyId ?? '', affordableWaitlistV2);

  const {
    enabled: waitlistEnabled,
    separateByBedroom,
    programs = [],
  } = waitlistConfigurationData ?? {};
  // One predicate, two jobs: whether to ask for this household's waitlist
  // record, and whether the card belongs on the page at all. `Boolean` is
  // load-bearing rather than decoration — the flag is undefined until
  // LaunchDarkly answers, and react-query reads an undefined `enabled` as
  // "enabled", which would fire the request on a property with no waitlist.
  const propertyRunsWaitlistV2 = Boolean(
    affordableWaitlistV2 && waitlistEnabled,
  );

  const {
    data: response,
    isLoading,
    refetch,
  } = useFetchAffordableWaitlist(
    organizationId ?? '',
    propertyId ?? '',
    applicationId ?? '',
    propertyRunsWaitlistV2,
  );

  const { data, incomeLevels, isLegacyV1 } = response || {};
  const hasWaitlistRecord = Boolean(data);
  // A V1 waitlist row survives the upgrade to V2 carrying no positions and empty V2 columns, and
  // answers this GET like any other record. Only the server can tell the two apart, so it says so
  // rather than letting the client read it off an empty positions array. Absent — an older
  // backend — reads as a V2 record, which is the behavior that shipped.
  const hasV2WaitlistRecord = hasWaitlistRecord && !isLegacyV1;

  // The card belongs to the property's waitlist, not to this household's place on it: an
  // applicant who has never been on the list still needs somewhere to be added from. The
  // backend answers a household with no record as `{ message }` and no `data`, which is why
  // requiring `data` here used to hide the card from exactly the households ENG-30211 is about.
  const isVisible = propertyRunsWaitlistV2;

  const waitlistSummary = isVisible
    ? {
        submissionDateTime: data?.applicationDateTime ?? null,
        householdIncome: data?.anticipatedAnnualIncome ?? null,
        totalAdults: data?.totalAdults ?? null,
        totalMinors: data?.totalMinors ?? null,
        position: data?.position ?? null,
        separateByBedroom: Boolean(waitlistEnabled && separateByBedroom),
        affordableWaitlistPositions: mapAffordableWaitlistRanks(
          data?.affordableWaitlistPositions,
        ),
        incomeLevels: toIncomeLevelRows(
          incomeLevels,
          toAtomicProgramNames(programs),
        ),
        preferredBedroomsText: getPreferredBedroomsText(prospectPreferences),
        preferredFloorPlanName: getPreferredFloorPlanName(
          prospectPreferences,
          floorPlans,
        ),
        waitlistPreferencesText: formatWaitlistPreferences(data?.preferences),
        displacement: data?.displacement ?? null,
        prefersAccessibleUnit: Boolean(
          prospectPreferences?.prefersUnitAccessible,
        ),
        // Tri-state on purpose: no record means the question was never asked, and rendering it
        // as No would assert an answer the household never gave. This deliberately keys off
        // `hasWaitlistRecord`, not `hasV2WaitlistRecord`: a legacy V1 row still carries the
        // household's real answer, and swapping in the V2-only predicate would dash out an
        // answer they actually gave.
        requestingAccommodation: hasWaitlistRecord
          ? Boolean(data.requestingAccommodation)
          : null,
        accommodationRequested: data?.accommodationRequested ?? null,
      }
    : null;

  return {
    waitlistSummary,
    isVisible,
    hasWaitlistRecord,
    hasV2WaitlistRecord,
    isLoading: isConfigurationLoading || isLoading,
    affordableWaitlistId: data?.id ?? null,
    refetch,
    separateByBedroom: Boolean(waitlistEnabled && separateByBedroom),
    waitlistV2Programs: programs,
  };
}

// The stored enum spells cancelled with two Ls; every screen a property manager reads spells it
// with one. Display copy is deliberately not the wire value; a status added server-side falls
// back to its raw enum until it is given a label here.
const POSITION_STATUS_MESSAGE = {
  WAITING: messages.positionStatusWaiting,
  SELECTED: messages.positionStatusSelected,
  HOUSED: messages.positionStatusHoused,
  WITHDRAWN: messages.positionStatusWithdrawn,
  DENIED: messages.positionStatusDenied,
  CANCELLED: messages.positionStatusCanceled,
};

// Own-property lookup only: a status colliding with an Object.prototype member would otherwise
// resolve to that member and render as a descriptor with no id.
export const getPositionStatusMessage = (status) =>
  Object.prototype.hasOwnProperty.call(POSITION_STATUS_MESSAGE, status)
    ? POSITION_STATUS_MESSAGE[status]
    : null;

export const mapPositionsToRows = (
  affordableWaitlistPositions,
  { applicationId, affordableWaitlistId, separateByBedroom },
) =>
  (affordableWaitlistPositions ?? [])
    .map((row) => {
      const isCanceled = row.status?.toLowerCase() === 'cancelled';
      const isSelected = row.status?.toLowerCase() === 'selected';

      if (row.bedroomCount === null) {
        return {
          key: row.bedroomCount,
          label: 'All Bedrooms',
          rank: row.rank,
          id: row.id,
          applicationId,
          bedroomCount: row.bedroomCount,
          status: row.status,
          statusMessage: getPositionStatusMessage(row.status),
          isCanceled,
          isSelected,
          affordableWaitlistId,
        };
      }

      if (separateByBedroom) {
        return {
          key: row.bedroomCount,
          label: `${row.bedroomCount} Bedroom`,
          rank: row.rank,
          id: row.id,
          applicationId,
          bedroomCount: row.bedroomCount,
          status: row.status,
          statusMessage: getPositionStatusMessage(row.status),
          isCanceled,
          isSelected,
          affordableWaitlistId,
        };
      }

      return null;
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank);

// Mirrors ADDABLE_STATUSES in fortress-backend's AffordableWaitlistEdits.ts. `New Portal App` is
// in the endpoint's set and deliberately not in this one: ENG-31969 routes that status through the
// Application Status dropdown instead of the card. A subset is the safe direction — the card can
// only refuse something the endpoint would have taken, never the reverse.
const ADDABLE_STATUSES = new Set(['In Process', 'Waitlist']);

export function useWaitlistCard({
  organizationId,
  propertyId,
  applicationId,
  prospectPreferences,
  floorPlans,
  onRefetch,
  applicationStatusName,
}) {
  const isDeniedOrCanceled =
    applicationStatusName === 'Denied' || applicationStatusName === 'Canceled';
  const isWaitlistActive = applicationStatusName === 'Waitlist';
  // Correcting the record only means something while the household is still on
  // the list. Once they have been selected into In Process, and anywhere past
  // it, the entry is history and the leasing record has taken over.
  const isEditable = isWaitlistActive;
  // Denying or cancelling a waitlist entry only means anything while the
  // household is still on the list; past that the leasing record owns the
  // outcome, so the row is reclaimed rather than shown inert.
  const showDenyCancel = isWaitlistActive;

  const canEditWaitlist = useHasPermission('prospect-update');
  const canReinstate = useHasPermission('affordable-waitlist-reinstate');
  const queryClient = useQueryClient();

  const { hasAffordableWaitlistPermissionAdd } =
    useAffordableWaitlistValidations();
  const { isEntryBlocked } = useWaitlistApplicationWindow();

  // `expanded` cannot be seeded from state that arrives with the query: a useState initializer
  // runs once at mount, before `hasV2WaitlistRecord` is known. Deriving it, with the caret's own
  // choice taking precedence, is what makes the no-record card open collapsed — and fixes the
  // same staleness for the denied/cancelled default.
  const [expandedOverride, setExpandedOverride] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDenyOpen, setIsDenyOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isSelectFromWaitlistOpen, setIsSelectFromWaitlistOpen] =
    useState(false);
  const [selectFromWaitlistModalState, setSelectFromWaitlistModalState] =
    useState(null);
  const [isReinstateOpen, setIsReinstateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const {
    waitlistSummary,
    isVisible,
    hasWaitlistRecord,
    hasV2WaitlistRecord,
    isLoading,
    affordableWaitlistId,
    refetch,
    waitlistV2Programs,
  } = useWaitlistSummary(
    organizationId,
    propertyId,
    applicationId,
    prospectPreferences,
    floorPlans,
  );

  // `isVisible` here means "render the card": also false while the request is in flight, so an
  // applicant who is on the list never flashes "Not on Waitlist" before their positions land.
  if (!isVisible || isLoading) {
    return { isVisible: false };
  }

  const expanded =
    expandedOverride ?? (hasV2WaitlistRecord && !isDeniedOrCanceled);
  const setExpanded = setExpandedOverride;

  // Holding a V2 waitlist record is the fact that separates Add from Deny/Cancel/Reinstate, and a
  // legacy V1 row does not count as one: it carries no position to deny, cancel or reinstate.
  // Denied and Canceled stay out of ADDABLE_STATUSES because the add writes the application's
  // status, and un-denying a household that way is a product decision nobody has made.
  const showAdd =
    !hasV2WaitlistRecord &&
    ADDABLE_STATUSES.has(applicationStatusName) &&
    hasAffordableWaitlistPermissionAdd;
  // Correcting the record only means something once there is a V2 record to correct. Without this
  // the pencil shows on a household whose edit save the endpoint refuses with NOT_ON_WAITLIST.
  const showEdit = canEditWaitlist && isEditable && hasV2WaitlistRecord;
  const isAddDisabled = isEntryBlocked;

  const handleActionSuccess = () => {
    setExpanded(false);
    refetch();
    onRefetch();
  };

  // Selecting sets the household's move-in date, which the qualification tabs read
  // as their pre-assignment effective date; they cache in React Query, outside the
  // parent refetch.
  const handleSelectFromWaitlistSuccess = () => {
    handleActionSuccess();
    invalidateAffordableQualificationQueries(queryClient);
  };

  const handleReinstateSuccess = () => {
    setExpanded(true);
    refetch();
    onRefetch();
  };

  // Same shape as reinstate's, and deliberately the same function: the household is now on the
  // list, so open the card on the record it just gained and refetch both layers. The application
  // status changed to Waitlist, and the parent refetch is what re-gates the buttons to
  // Deny/Cancel.
  const handleAddSuccess = handleReinstateSuccess;

  // Editing neither collapses nor expands the card: the agent stays where
  // they were and the numbers under them update.
  const handleEditSuccess = () => {
    refetch();
    onRefetch();
  };

  const handleOpenSelectFromWaitlist = (row) => {
    setSelectFromWaitlistModalState({
      affordableWaitlistId: row.affordableWaitlistId,
      affordableWaitlistRow: {
        id: row.id,
        rank: row.rank,
        applicationId: row.applicationId,
        bedroomCount: row.bedroomCount,
      },
    });
    setIsSelectFromWaitlistOpen(true);
  };

  const { separateByBedroom, affordableWaitlistPositions } = waitlistSummary;

  // A position that has left the list — the whole application denied or canceled, or this one
  // canceled or selected — shows its status in place of a rank and the Select link.
  const positionRows = mapPositionsToRows(affordableWaitlistPositions, {
    applicationId,
    affordableWaitlistId,
    separateByBedroom,
  }).map((row) => ({
    ...row,
    showsStatus: isDeniedOrCanceled || row.isCanceled || row.isSelected,
  }));

  return {
    isVisible: true,
    waitlistSummary,
    hasWaitlistRecord,
    hasV2WaitlistRecord,
    isDeniedOrCanceled,
    isWaitlistActive,
    expanded,
    setExpanded,
    positionRows,
    affordableWaitlistId,
    waitlistV2Programs,
    showAdd,
    isAddDisabled,
    isAddOpen,
    setIsAddOpen,
    isDenyOpen,
    setIsDenyOpen,
    isCancelOpen,
    setIsCancelOpen,
    isSelectFromWaitlistOpen,
    setIsSelectFromWaitlistOpen,
    selectFromWaitlistModalState,
    isReinstateOpen,
    setIsReinstateOpen,
    isEditOpen,
    setIsEditOpen,
    showEdit,
    showDenyCancel,
    canReinstate,
    handleActionSuccess,
    handleSelectFromWaitlistSuccess,
    handleReinstateSuccess,
    handleAddSuccess,
    handleEditSuccess,
    handleOpenSelectFromWaitlist,
  };
}
